import React, { useState, useRef } from "react";
import PhoneNumberInput from '../../../../components/common/PhoneNumberInput';
import AppFormInput from '../../../../components/ui/AppFormInput';
import AppFormSelect from '../../../../components/ui/AppFormSelect';
import apiClient from "../../../../services/api-client";
import { Toast } from 'primereact/toast';
import { InputSwitch } from "primereact/inputswitch";
import { confirmDialog } from 'primereact/confirmdialog';
import { useMountEffect } from "primereact/hooks";
import StorageHelper from '../../../../utils/storage-helper';

const validateEmail = (email) => /^[^\s@]+@[^\s@.]+\.[^\s@]+$/.test(email);

const PHONE_RULES = {
    create: { pattern: /^\d{9,15}$/, message: 'Please enter a valid phone number' },
    edit: { pattern: /^\d{5,}$/, message: 'Phone number should be more than 5 digits' },
};

const STATUS_COPY = {
    create: {
        toastActive: 'User will be created active',
        toastDisabled: 'User will be created disabled',
        labelDisabled: 'Create and set it disabled?',
        labelActive: 'Activate account upon creation?',
    },
    edit: {
        toastActive: 'User will be active',
        toastDisabled: 'User will be disabled',
        labelDisabled: 'Account is disabled?',
        labelActive: 'Account is active?',
    },
};

const requiredText = (value, message) => (value?.trim() ? '' : message);

const validateEmailField = (email) => {
    if (!email.trim()) return 'Email is required';
    return validateEmail(email) ? '' : 'Invalid email format';
};

const validatePhoneField = (phoneNumber, rule) => {
    if (!phoneNumber.trim()) return 'Phone number is required';
    return rule.pattern.test(phoneNumber) ? '' : rule.message;
};

/**
 * Register a new employee, or edit one when `employeeData` is given.
 */
const UserForm = ({ onCloseDialog, employeeData = null }) => {
    const isEdit = employeeData !== null;
    const [firstName, setFirstName] = useState(employeeData?.firstName ?? '');
    const [lastName, setLastName] = useState(employeeData?.lastName ?? '');
    const [email, setEmail] = useState(employeeData?.emailAddress ?? '');
    const [idNumber, setIdNumber] = useState(employeeData?.idnumber ?? '');
    const [phoneNumber, setPhoneNumber] = useState(employeeData?.phoneNumber ?? '');
    const [userStatus, setUserStatus] = useState(employeeData?.isActive ?? false);
    const [userRole, setUserRole] = useState(employeeData?.userRole?.id ?? '');
    const [selectedRole, setSelectedRole] = useState(null);
    const [errors, setErrors] = useState({});
    const [options, setOptions] = useState([]);
    const toast = useRef(null);

    useMountEffect(() => {
        const fetchRoles = async () => {
            try {
                const response = await apiClient.get('system/roles', {
                    headers: {
                        Authorization: 'Bearer ' + StorageHelper.get('token'),
                        'Content-Type': 'application/json'
                    }
                });
                setOptions(response.data.responseBody.map(element => ({
                    label: element.name,
                    value: element.id,
                    description: element.description
                })));
            } catch (error) {
                console.error(error);
            }
        };
        fetchRoles();
    });

    const saveUser = async () => {
        const userPayload = {
            "operation": "create-user-profile",
            "requestBody": {
                ...(isEdit && { "userId": employeeData.userId }),
                "firstName": firstName,
                "lastName": lastName,
                "emailAddress": email,
                "phoneNumber": phoneNumber,
                "idnumber": idNumber,
                "isActive": true,
                "locked": false,
                "enabled": true,
                "userType": "EMPLOYEE",
                "userRole": userRole
            }
        };

        const config = { headers: { "Authorization": `Bearer ${StorageHelper.get('token')}` } };

        try {
            const response = isEdit
                ? await apiClient.put('user/edit', userPayload, config)
                : await apiClient.post('user/create/employee', userPayload, config);

            if (response.data.status === 'FAILED') {
                toast.current.show({ severity: 'error', sticky: true, summary: 'Failed', detail: response.data.message });
            } else {
                onCloseDialog({ success: true });
            }
        } catch (error) {
            console.error(error);
        }
    };

    const validateUser = () => {
        const newErrors = {
            firstName: requiredText(firstName, 'First name is required'),
            lastName: requiredText(lastName, 'Last name is required'),
            email: validateEmailField(email),
            phoneNumber: validatePhoneField(phoneNumber, isEdit ? PHONE_RULES.edit : PHONE_RULES.create),
        };
        const valid = Object.values(newErrors).every((message) => message === '');
        setErrors(valid ? {} : newErrors);
        return valid;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validateUser()) return;

        if (isEdit) {
            confirmDialog({
                message: "Are you sure you want to update this?",
                header: 'Confirmation',
                icon: 'pi pi-exclamation-triangle',
                accept: saveUser
            });
        } else {
            saveUser();
        }
    };

    const handleUserRoleChange = (val) => {
        setUserRole(val);
        setSelectedRole(options.find((element) => element.value === val) ?? null);
    };

    const copy = STATUS_COPY[isEdit ? 'edit' : 'create'];
    const statusToast = (active) => (active ? copy.toastActive : copy.toastDisabled);
    const statusLabel = userStatus ? copy.labelDisabled : copy.labelActive;

    return (
        <>
            <Toast ref={toast} />
            <div className="w-full mt-2">
                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                    <AppFormInput label="First Name" id="firstName" value={firstName} onChange={(e) => setFirstName(e.target.value)} error={errors.firstName} />

                    <AppFormInput label="Last Name" id="lastName" value={lastName} onChange={(e) => setLastName(e.target.value)} error={errors.lastName} />

                    <AppFormInput label="Email" id="email" value={email} onChange={(e) => setEmail(e.target.value)} error={errors.email} />

                    <AppFormInput label="National ID Number" id="idNumber" value={idNumber} onChange={(e) => setIdNumber(e.target.value)} error={errors.idNumber} />

                    <PhoneNumberInput label="Phone" value={phoneNumber} onChange={(val) => setPhoneNumber(val)} error={errors.phoneNumber} />

                    <AppFormSelect
                        label="User Role"
                        id="userRole"
                        value={userRole}
                        options={options}
                        optionLabel="label"
                        optionValue="value"
                        onChange={(e) => handleUserRoleChange(e.target.value)}
                        placeholder="Select a role"
                        hint={selectedRole?.description}
                    />

                    <div className="form-control w-full mt-2">
                        <label className="label cursor-pointer justify-start gap-4 p-4 border border-base-200 rounded-lg bg-base-50/50 hover:bg-base-100 transition-colors">
                            <InputSwitch
                                id="value"
                                name="value"
                                checked={userStatus}
                                onChange={(e) => {
                                    toast.current.show({ severity: 'info', summary: 'User Status', detail: statusToast(!userStatus) });
                                    setUserStatus(e.value);
                                }}
                            />
                            <span className="label-text font-medium text-gray-700">{statusLabel}</span>
                        </label>
                    </div>

                    <div className="mt-6 flex justify-end pt-4 border-t border-base-200">
                        <button type="submit" className="btn btn-primary px-8 text-white rounded-lg shadow-sm">
                            <i className="pi pi-check"></i> {isEdit ? 'Update User' : 'Register User'}
                        </button>
                    </div>
                </form>
            </div>
        </>
    );
};

export default UserForm;
